import { IPersonDetailsDrawerInfo } from '.';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { REGEX } from '@utils/validationUtils';

export const initialValues: IPersonDetailsDrawerInfo = {
  type: '',
  companyName: '',
  title: '',
  titleId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  surname: '',
  initials: '',
  dateOfBirth: '',
  nationality: '',
  nationalityId: '',
  email: '',
  phoneType: '',
  phoneNumber: '',
  relativeOf: '',
  smoker: 'NO',
  weight: 0,
  height: 0,
  dateOfDeath: '',
  dateOfDeathNotified: '',
  taxable: false,
  financialReference: '',
  occupationClass: '',
  occupationClassCode: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  salary: 0,
  dateOfSelfCertificateReceived: '',
  dateOfSelfCertificateValidated: '',
  category: '',
  dateOfKYC: '',
  comments: '',
};

export const getSections = (
  data: IPersonDetailsDrawerInfo,
  lists: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isCompany = data?.type?.toUpperCase() === 'COMPANY';
  const isPerson = data?.type?.toUpperCase() === 'PERSON';

  return [
    {
      sectionTitle: 'General Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          type: FormFieldType.select,
          placeholder: 'Type',
          value: data?.type,
          disabled: false,
          required: true,
          selectOptions: lists.SalesforceManagement_PersonTypes,
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: data?.companyName,
          required: isCompany,
          disabled: !isCompany,
          hidden: !isCompany,
          minCharacters: 2,
          maxCharacters: 300,
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          value: data?.titleId || data?.title,
          required: isPerson,
          disabled: !isPerson,
          hidden: !isPerson,
          selectOptions: lists.titles,
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: data?.firstName,
          required: isPerson,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 2,
          maxCharacters: 100,
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: data?.middleName,
          required: isPerson,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 1,
          maxCharacters: 100,
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: data?.lastName,
          required: isPerson,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 2,
          maxCharacters: 100,
        },
        surname: {
          name: 'surname',
          title: 'Maiden Name',
          placeholder: 'Maiden Name',
          type: FormFieldType.text,
          value: data?.surname,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 2,
          maxCharacters: 100,
        },
        initials: {
          name: 'initials',
          title: 'Initials',
          placeholder: 'Initials',
          type: FormFieldType.text,
          value: data?.initials,
          required: false,
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: data?.dateOfBirth,
          disabled: !isPerson,
          hidden: !isPerson,
          maxDate: new Date(),
        },
        nationality: {
          name: 'nationality',
          title: 'Nationality',
          placeholder: 'Nationality',
          type: FormFieldType.select,
          value: data?.nationalityId || data?.nationality,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          selectOptions: lists.nationalities,
        },
      },
    },
    {
      sectionTitle: 'Contact Details',
      inputs: {
        email: {
          name: 'email',
          title: 'Email',
          type: FormFieldType.text,
          placeholder: 'Email',
          value: data?.email,
          required: false,
          disabled: false,
          customValidator: (values) => {
            if (values.email && !REGEX.email.test(values.email)) {
              return 'Invalid email';
            }
            return '';
          },
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          value: data?.phoneType,
          required: false,
          selectOptions: lists.phoneType,
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Phone Number',
          value: data?.phoneNumber,
          required: false,
          editCountryCode: false,
        },
        relativeOf: {
          name: 'relativeOf',
          title: 'Relative of',
          type: FormFieldType.text,
          placeholder: 'Relative of',
          value: data?.relativeOf,
          required: false,
          minCharacters: 2,
          maxCharacters: 200,
        },
      },
    },
    {
      ...(isPerson && { sectionTitle: 'Health Details' }),
      inputs: {
        smoker: {
          name: 'smoker',
          title: 'Smoker',
          type: FormFieldType.select,
          placeholder: 'Smoker',
          value: data?.smoker,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          selectOptions: lists.isSmoker,
        },
        weight: {
          name: 'weight',
          title: 'Weight (kg)',
          type: FormFieldType.number,
          placeholder: 'Weight (kg)',
          value: data?.weight,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          minValue: 0,
          maxValue: 300,
        },
        height: {
          name: 'height',
          title: 'Height (cm)',
          type: FormFieldType.number,
          placeholder: 'Height (cm)',
          value: data?.height,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          minValue: 0,
          maxValue: 300,
        },
        dateOfDeath: {
          name: 'dateOfDeath',
          title: 'Date of Death',
          placeholder: 'Date of Death',
          type: FormFieldType.date,
          value: data?.dateOfDeath,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        dateOfDeathNotified: {
          name: 'dateOfDeathNotified',
          title: 'Date of Death Notified',
          placeholder: 'Date of Death Notified',
          type: FormFieldType.date,
          value: data?.dateOfDeathNotified,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
      },
    },
    {
      sectionTitle: 'Business Details',
      inputs: {
        taxable: {
          name: 'taxable',
          title: 'Taxable',
          placeholder: 'Taxable',
          type: FormFieldType.switch,
          value: data?.taxable,
        },
        financialReference: {
          name: 'financialReference',
          title: 'Financial Reference',
          placeholder: 'Financial Reference',
          type: FormFieldType.text,
          value: data?.financialReference,
        },
        occupationClass: {
          name: 'occupationClass',
          title: 'Occupation Class',
          placeholder: 'Occupation Class',
          type: FormFieldType.select,
          value: data?.occupationClassCode || data?.occupationClass,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          selectOptions: lists.occupationClasses,
        },
        profession: {
          name: 'profession',
          title: 'Profession',
          placeholder: 'Profession',
          type: FormFieldType.text,
          value: data?.profession,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 2,
          maxCharacters: 100,
        },
        employer: {
          name: 'employer',
          title: 'Employer / Institute',
          placeholder: 'Employer / Institute',
          type: FormFieldType.text,
          value: data?.employer,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          minCharacters: 2,
          maxCharacters: 100,
        },
        dateOfEmployment: {
          name: 'dateOfEmployment',
          title: 'Date of Employment',
          placeholder: 'Date of Employment',
          type: FormFieldType.date,
          value: data?.dateOfEmployment,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        salary: {
          name: 'salary',
          title: 'Salary',
          type: FormFieldType.currency,
          placeholder: 'Salary',
          value: data?.salary,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        dateOfSelfCertificateReceived: {
          name: 'dateOfSelfCertificateReceived',
          title: 'Date of Self Certificate Received',
          placeholder: 'Date of Self Certificate Received',
          type: FormFieldType.date,
          value: data?.dateOfSelfCertificateReceived,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        dateOfSelfCertificateValidated: {
          name: 'dateOfSelfCertificateValidated',
          title: 'Date of Self Certificate Validated',
          placeholder: 'Date of Self Certificate Validated',
          type: FormFieldType.date,
          value: data?.dateOfSelfCertificateValidated,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        category: {
          name: 'category',
          title: 'Category',
          placeholder: 'Category',
          type: FormFieldType.select,
          value: data?.category,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
          selectOptions: lists.personsCategories,
        },
        dateOfKYC: {
          name: 'dateOfKYC',
          title: 'Date of KYC',
          placeholder: 'Date of KYC',
          type: FormFieldType.date,
          value: data?.dateOfKYC,
          required: false,
          disabled: !isPerson,
          hidden: !isPerson,
        },
        comments: {
          name: 'comments',
          title: 'Comments',
          placeholder: 'Comments',
          type: FormFieldType.textArea,
          value: data?.comments,
        },
      },
    },
  ];
};
